import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, Users, TrendingUp, MessageSquare, Star, Shield, BarChart3, Download, ArrowLeft, RefreshCw, Eye, ArrowUpRight, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminReports() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchReports();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchReports = async () => {
    setRefreshing(true);
    try {
      const [overviewRes, conversionRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/reports/overview`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/reports/conversion`, { withCredentials: true })
      ]);
      setOverview(overviewRes.data);
      setConversion(conversionRes.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleExport = async (reportType) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/reports/export?report_type=${reportType}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${reportType} report downloaded`);
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Platform Reports</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchReports}
              className="btn-secondary text-sm flex items-center gap-2"
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <span className="text-sm text-slate-600">{user?.name}</span>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-slate-200 pb-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'overview'
                ? 'bg-orange-500 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="tab-overview"
          >
            <BarChart3 className="h-4 w-4" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('conversion')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'conversion'
                ? 'bg-orange-500 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="tab-conversion"
          >
            <TrendingUp className="h-4 w-4" />
            Conversion Metrics
          </button>
          <button
            onClick={() => setActiveTab('exports')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'exports'
                ? 'bg-orange-500 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="tab-exports"
          >
            <Download className="h-4 w-4" />
            Export Data
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && overview && (
          <div className="space-y-6" data-testid="overview-section">
            {/* User Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => navigate('/admin/user-control')}
                className="card hover:border-orange-500 hover:shadow-md transition-all cursor-pointer text-left group"
                data-testid="stat-total-users"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Total Users</p>
                      <p className="text-2xl font-bold text-slate-900">{overview.users.total}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-orange-500 transition-colors" />
                </div>
                <div className="mt-3 flex gap-4 text-sm">
                  <span className="text-green-600">+{overview.users.new_7d} this week</span>
                  <span className="text-slate-500">+{overview.users.new_30d} this month</span>
                </div>
                <p className="mt-2 text-xs text-slate-400 group-hover:text-orange-500">Click to view all users →</p>
              </button>

              <button
                onClick={() => navigate('/admin/user-control?user_type=tradesperson')}
                className="card hover:border-orange-500 hover:shadow-md transition-all cursor-pointer text-left group"
                data-testid="stat-tradespeople"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
                      <Users className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Tradespeople</p>
                      <p className="text-2xl font-bold text-slate-900">{overview.users.tradespeople}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-orange-500 transition-colors" />
                </div>
                <div className="mt-3 text-sm text-slate-500">
                  {overview.engagement.profile_completion_rate}% profile completion
                </div>
                <p className="mt-2 text-xs text-slate-400 group-hover:text-orange-500">Click to view tradespeople →</p>
              </button>

              <button
                onClick={() => navigate('/admin/user-control?user_type=employer')}
                className="card hover:border-orange-500 hover:shadow-md transition-all cursor-pointer text-left group"
                data-testid="stat-employers"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Employers</p>
                      <p className="text-2xl font-bold text-slate-900">{overview.users.employers}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-orange-500 transition-colors" />
                </div>
                <div className="mt-3 text-sm text-slate-500">
                  {overview.engagement.employer_subscription_rate}% subscribed
                </div>
                <p className="mt-2 text-xs text-slate-400 group-hover:text-orange-500">Click to view employers →</p>
              </button>

              <button
                onClick={() => navigate('/admin/user-control?account_status=active')}
                className="card hover:border-orange-500 hover:shadow-md transition-all cursor-pointer text-left group"
                data-testid="stat-subscriptions"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Active Subscriptions</p>
                      <p className="text-2xl font-bold text-slate-900">{overview.active_subscriptions}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-orange-500 transition-colors" />
                </div>
                <p className="mt-3 text-xs text-slate-400 group-hover:text-orange-500">Click to view subscribed users →</p>
              </button>
            </div>

            {/* Engagement Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/admin/support-inbox')}
                className="card hover:border-orange-500 hover:shadow-md transition-all cursor-pointer text-left group"
                data-testid="stat-messages"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
                      <MessageSquare className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Messages</p>
                      <p className="text-2xl font-bold text-slate-900">{overview.engagement.total_messages}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-orange-500 transition-colors" />
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Last 7 days</span>
                    <span className="font-medium">{overview.engagement.messages_7d}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Last 30 days</span>
                    <span className="font-medium">{overview.engagement.messages_30d}</span>
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-400 group-hover:text-orange-500">Click to view message threads →</p>
              </button>

              <button
                onClick={() => navigate('/admin/reviews')}
                className="card hover:border-orange-500 hover:shadow-md transition-all cursor-pointer text-left group"
                data-testid="stat-reviews"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-yellow-100 rounded-lg group-hover:bg-yellow-200 transition-colors">
                      <Star className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Reviews</p>
                      <p className="text-2xl font-bold text-slate-900">{overview.reviews.total}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-orange-500 transition-colors" />
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Last 30 days</span>
                    <span className="font-medium">{overview.reviews.reviews_30d}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Platform avg rating</span>
                    <span className="font-medium flex items-center gap-1">
                      {overview.reviews.platform_avg_rating}
                      <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                    </span>
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-400 group-hover:text-orange-500">Click to manage reviews →</p>
              </button>

              <button
                onClick={() => navigate('/admin/verification-queue')}
                className="card hover:border-orange-500 hover:shadow-md transition-all cursor-pointer text-left group"
                data-testid="stat-credentials"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                      <Shield className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Credentials</p>
                      <p className="text-2xl font-bold text-slate-900">{overview.credentials.verified}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-orange-500 transition-colors" />
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Verified</span>
                    <span className="font-medium text-green-600">{overview.credentials.verified}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Pending review</span>
                    <span className="font-medium text-orange-600">{overview.credentials.pending}</span>
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-400 group-hover:text-orange-500">Click to review credentials →</p>
              </button>
            </div>

            {/* Search Activity */}
            <div className="card" data-testid="stat-searches">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Eye className="h-5 w-5 text-orange-500" />
                Search Activity
              </h3>
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-3xl font-bold text-slate-900">{overview.engagement.searches_30d}</p>
                  <p className="text-sm text-slate-500">Profile appearances in last 30 days</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Conversion Tab */}
        {activeTab === 'conversion' && conversion && (
          <div className="space-y-6" data-testid="conversion-section">
            {/* Platform Metrics */}
            <div className="card">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-orange-500" />
                Platform Conversion Rate ({conversion.period.replace('_', ' ')})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-slate-900">{conversion.platform_metrics.total_profile_views}</p>
                  <p className="text-sm text-slate-600">Total Profile Views</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-slate-900">{conversion.platform_metrics.total_messages_sent}</p>
                  <p className="text-sm text-slate-600">Messages Sent</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
                  <p className="text-3xl font-bold text-orange-600">{conversion.platform_metrics.platform_conversion_rate}%</p>
                  <p className="text-sm text-orange-700">Conversion Rate</p>
                </div>
              </div>
            </div>

            {/* Top Performers */}
            <div className="card">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <ArrowUpRight className="h-5 w-5 text-green-500" />
                Top Performing Tradespeople
              </h3>
              {conversion.top_performers.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No conversion data available yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Tradesperson</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Profile Views</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Messages Received</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Conversion Rate</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversion.top_performers.map((tp, idx) => (
                        <tr 
                          key={tp.tradesperson_id} 
                          onClick={() => navigate(`/admin/user/${tp.tradesperson_id}`)}
                          className="border-b border-slate-100 hover:bg-orange-50 cursor-pointer transition-colors"
                          data-testid={`performer-row-${idx}`}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {idx < 3 && (
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                  idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                  idx === 1 ? 'bg-slate-100 text-slate-600' :
                                  'bg-orange-100 text-orange-700'
                                }`}>
                                  #{idx + 1}
                                </span>
                              )}
                              <span className="font-medium text-slate-900">{tp.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center text-slate-600">{tp.profile_views}</td>
                          <td className="py-3 px-4 text-center text-slate-600">{tp.messages_received}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`font-medium ${
                              tp.conversion_rate >= 20 ? 'text-green-600' :
                              tp.conversion_rate >= 10 ? 'text-orange-600' :
                              'text-slate-600'
                            }`}>
                              {tp.conversion_rate}%
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Eye className="h-4 w-4 text-slate-400 hover:text-orange-500 inline-block" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Export Tab */}
        {activeTab === 'exports' && (
          <div className="space-y-6" data-testid="exports-section">
            <div className="card">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Download className="h-5 w-5 text-orange-500" />
                Export Reports
              </h3>
              <p className="text-slate-600 mb-6">Download platform data as CSV files for further analysis.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <button
                  onClick={() => handleExport('users')}
                  className="p-4 border border-slate-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                  data-testid="export-users-btn"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-slate-900">Users Report</span>
                  </div>
                  <p className="text-sm text-slate-500">Export all user accounts with their details</p>
                </button>

                <button
                  onClick={() => handleExport('tradespeople')}
                  className="p-4 border border-slate-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                  data-testid="export-tradespeople-btn"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="h-5 w-5 text-orange-600" />
                    <span className="font-medium text-slate-900">Tradespeople Report</span>
                  </div>
                  <p className="text-sm text-slate-500">Export tradesperson profiles and stats</p>
                </button>

                <button
                  onClick={() => handleExport('messages')}
                  className="p-4 border border-slate-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                  data-testid="export-messages-btn"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <MessageSquare className="h-5 w-5 text-indigo-600" />
                    <span className="font-medium text-slate-900">Messages Report</span>
                  </div>
                  <p className="text-sm text-slate-500">Export messaging activity data</p>
                </button>

                <button
                  onClick={() => handleExport('reviews')}
                  className="p-4 border border-slate-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                  data-testid="export-reviews-btn"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Star className="h-5 w-5 text-yellow-600" />
                    <span className="font-medium text-slate-900">Reviews Report</span>
                  </div>
                  <p className="text-sm text-slate-500">Export all reviews and ratings</p>
                </button>

                <button
                  onClick={() => handleExport('subscriptions')}
                  className="p-4 border border-slate-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                  data-testid="export-subscriptions-btn"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-slate-900">Subscriptions Report</span>
                  </div>
                  <p className="text-sm text-slate-500">Export subscription and payment data</p>
                </button>

                <button
                  onClick={() => handleExport('credentials')}
                  className="p-4 border border-slate-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                  data-testid="export-credentials-btn"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="h-5 w-5 text-emerald-600" />
                    <span className="font-medium text-slate-900">Credentials Report</span>
                  </div>
                  <p className="text-sm text-slate-500">Export credential verification data</p>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
